<script setup lang="ts">
import { SwiperSlide } from "swiper/vue";
import { useCheckout } from "~/composables/extensions/useCheckout";
import { getTranslatedProperty } from "@shopware/helpers";

import { getRoute } from "~/lib/routes";
import type { Schemas } from "#shopware";

const { product } = defineProps<{
	product: Schemas["Product"];
}>();

const LOW_STOCK_THRESHOLD = 5;
const router = useRouter();

const { onViewItem } = useGtagEvents();

const { selectedShippingMethod } = useSessionContext();
const { selectedPickupLocation } = useCart();
const { addRecentlyViewedProduct } = useRecentlyViewed();

const title = computed<string>(() => getTranslatedProperty(product, "metaTitle") ?? product.name ?? "");
const description = computed<string>(() => getTranslatedProperty(product, "metaDescription") ?? "");

useCustomSeoMeta({ title, description, image: toRef(`https://kippie.nl/_image/branded/${product.cover?.media.url}`) });

useHead({
	title: title.value
});

useProductJsonLD(product);

const availableShippingMethods = computed(() =>
	product.extensions?.availableShippingMethodsExtension?.data?.map((method) =>
		useCheckout().shippingMethodsList.find((m) => m.id === method.id)
	)
);

const hasPickupOption = computed(() => {
	const currentShippingMethod = useCheckout().shippingMethodsList.find(
		(method) => method.id === selectedShippingMethod.value?.id
	);

	return currentShippingMethod?.technicalName === "delivery" || !selectedPickupLocation.value;
});

const isPickupOnly = computed(
	() => availableShippingMethods.value?.length === 1 && availableShippingMethods.value[0]?.technicalName === "pickup"
);

const isDeliveryOnly = computed(
	() =>
		availableShippingMethods.value?.length === 1 && availableShippingMethods.value[0]?.technicalName === "delivery"
);

const isOutOfStock = computed(() => product.stock < 1);

const goBack = () => {
	if (window.history.length > 1) {
		router.back();
	} else {
		navigateTo(getRoute("assortment"));
	}
};

const getMediaUrl = (media: Schemas["Media"]) => {
	const thumbnail = media.thumbnails?.find((thumbnail) => thumbnail.width === 800);

	if (!thumbnail) {
		console.warn("No thumbnail found for product", product.name);
		return media.url || "https://placehold.co/600x400";
	}

	return thumbnail.url;
};

onMounted(() => {
	try {
		onViewItem(product);
	} catch (error) {
		console.error("Error on view item", error);
	}
	addRecentlyViewedProduct(product.id);
});
</script>

<template>
	<section class="bg-yellow lg:bg-gradient-to-r from-yellow from-[40%] via-white via-[40%] to-white">
		<Container>
			<div class="flex flex-col lg:flex-row lg:gap-10">
				<div class="lg:w-7/12 relative bg-yellow py-6 lg:pr-10">
					<div class="w-full aspect-[7/6] sticky top-6">
						<KippieCarousel
							v-if="product.media?.length"
							class="w-full h-full"
							:navigation="!!product.media?.length"
							:pagination="!!product.media?.length"
						>
							<SwiperSlide
								class="!flex items-center justify-center"
								v-for="productImage in [...product.media].sort(
									(a, b) => (a.position ?? 0) - (b.position ?? 0)
								)"
								:key="productImage.id"
							>
								<!-- {{ media.media.thumbnails[0] }} -->
								<img
									class="max-w-[70%] w-full"
									:src="getMediaUrl(productImage.media)"
									:alt="getTranslatedProperty(product, 'name')"
									height="600"
								/>
							</SwiperSlide>
						</KippieCarousel>
						<div class="lg:absolute lg:bottom-6 flex gap-1">
							<KippieLabel
								v-if="!!product.customFields?.kippie_product_fields_first_label"
								:label="product.customFields.kippie_product_fields_first_label as string"
							/>
							<KippieLabel
								v-if="!!product.customFields?.kippie_product_fields_second_label"
								:label="product.customFields.kippie_product_fields_second_label as string"
							/>
						</div>
						<div class="absolute top-0 w-full flex justify-between items-center z-10">
							<KippieIconButton icon="arrow-back" @click="goBack" />
						</div>
					</div>
				</div>
				<div class="lg:w-5/12 -mx-6 rounded-t-lg px-6 flex flex-col gap-6 py-6 bg-white">
					<div class="flex flex-col gap-2">
						<div class="flex justify-between items-center gap-4">
							<Typography variant="h1">
								{{ getTranslatedProperty(product, "name") }}
							</Typography>
							<ProductPrice :product="product" />
						</div>
						<div class="flex gap-2">
							<KippieReviewStars :value="Math.round(product.ratingAverage || 4)" />
							<ProductReviewCounter :product="product" />
						</div>
					</div>

					<div class="flex flex-col gap-4">
						<KippieLabel v-if="isPickupOnly" label="Alleen in de winkel" color="gray" />
						{{ getTranslatedProperty(product, "metaDescription") }}
					</div>

					<LazyProductStockNotification
						v-if="product.stock < LOW_STOCK_THRESHOLD"
						:product="product"
						:low-stock-threshold="LOW_STOCK_THRESHOLD"
					/>

					<Divider />

					<ProductConfigurator />

					<div class="flex flex-col gap-2">
						<AddToCartButton :product-prop="product" v-slot="{ onAddToCart }">
							<KippieButton
								color="yellow"
								block
								justify="space-between"
								:disabled="isOutOfStock"
								@click="onAddToCart"
							>
								In winkelmandje
								<template #right>
									<SvgIcon name="shopping-bag" />
								</template>
							</KippieButton>
						</AddToCartButton>
						<AddToCartButton :product-prop="product" v-slot="{ onAddToCart }" v-if="hasPickupOption">
							<KippieButton
								color="white"
								block
								justify="space-between"
								:disabled="isOutOfStock"
								@click="onAddToCart($event, true)"
								v-if="!isDeliveryOnly"
							>
								Afhalen in winkel
								<template #right>
									<SvgIcon name="store" />
								</template>
							</KippieButton>
						</AddToCartButton>
					</div>

					<ProductUsp :values="product.customFields?.kippie_product_fields_unique_selling_points" />

					<KippieAccordion
						id="product-detail-accordion"
						border-bottom
						multiple
						:sections="['description', 'preparation', 'reviews', 'specifications']"
						:default-open="['description']"
						class="bg-white text-black-light"
					>
						<template #title-description>
							<h3 class="text-base font-bold">Omschrijving</h3>
						</template>
						<template #content-description>
							<span class="" v-html="getTranslatedProperty(product, 'description')"> </span>
						</template>

						<template #title-preparation>
							<h3 class="text-base font-bold">Bereiding</h3>
						</template>
						<template #content-preparation>
							<span
								v-if="product.customFields?.kippie_product_fields_preparation"
								v-html="
									getTranslatedProperty(product.customFields, 'kippie_product_fields_preparation')
								"
							>
							</span>
							<span v-else>
								<p>Geen bereidingsinstructies beschikbaar</p>
							</span>
						</template>

						<template #title-reviews>
							<h3 class="text-base font-bold">Beoordelingen</h3>
						</template>
						<template #content-reviews>
							<ProductReviews :product="product" />
						</template>

						<template #title-specifications>
							<h3 class="text-base font-bold">Specificaties</h3>
						</template>
						<template #content-specifications>
							<ProductSpecifications
								v-if="product.properties && product.properties.length"
								:properties="product.properties || []"
							>
								<div
									class="flex flex-col"
									v-if="product.extensions?.allergens && product.extensions?.allergens.length"
								>
									<span class="font-bold">Allergenen</span>
									{{ product.extensions.allergens.map((allergen) => allergen.name).join(", ") }}
								</div>
							</ProductSpecifications>
							<span v-else>
								<p>Geen specificaties beschikbaar</p>
							</span>
						</template>
					</KippieAccordion>
				</div>
			</div>
		</Container>

		<CrossSellSection :product="product" />

		<div class="fixed w-full bottom-0 bg-sand p-4 flex gap-2 lg:hidden">
			<AddToCartButton :product-prop="product" v-slot="{ onAddToCart }">
				<KippieButton block size="small" :disabled="isOutOfStock" @click="onAddToCart">
					In winkelmandje
				</KippieButton>
			</AddToCartButton>
		</div>
	</section>
</template>
